import type { AuthContextProps } from 'react-oidc-context';
import { RETURN_TO_KEY } from '../components/ProtectedRoute';
import { sameOriginPath } from './returnPath';

/**
 * The label of the button that starts it, wherever that button is: the sign-in card's
 * `signInCopy.google` is this, and the intake page's "sign in with Google again" answer
 * (#2408) uses it rather than a label of its own.
 */
export const GOOGLE_SIGN_IN_LABEL = 'Continue with Google';

/**
 * The one way this app starts a sign-in with Google: the sign-in card's Google button
 * (`signin/SignInModule.tsx`) and `/login` (`pages/Login.tsx`, when the snapshot form's "sign
 * in with Google again" answer, crossmarc/steward-app#2408, signed out and asked for Google
 * after it) both call this, so the two cannot drift apart. It lives
 * outside `signin/` because that folder is imported from outside only through its
 * `index.ts`, which exports the card and the page and nothing else
 * (`signin/signinModule.structure.test.ts`).
 *
 * `/callback` reads `RETURN_TO_KEY` to decide where to land. Only a same-origin path is
 * stored; without one, whatever is already stored there is left alone. Then the app's
 * ordinary authorization-code redirect, asking the service to hand off to Google
 * (`prompt: 'select_account'`, so Google lets the person choose the account). It returns
 * through `/callback` like any hosted sign-in, with a new set of tokens.
 *
 * This does not sign anybody out. A caller that must drop the login it holds first does so
 * itself, before calling this.
 */
export async function startGoogleSignIn(
  auth: Pick<AuthContextProps, 'signinRedirect'>,
  returnTo: string | null | undefined,
): Promise<void> {
  const path = sameOriginPath(returnTo);
  if (path) {
    try {
      sessionStorage.setItem(RETURN_TO_KEY, path);
    } catch {
      // No session storage (private browsing): the callback lands on the home page.
    }
  }
  await auth.signinRedirect({ extraQueryParams: { identity_provider: 'Google', prompt: 'select_account' } });
}
